import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { conversationService, CONVERSATION_PAGE_SIZE } from '../services/conversationService';
import { useConversationStore } from '../../../stores';
import { Conversation } from '../../../types';
import { useSocketEvent, SOCKET_EVENTS } from '../../../lib/websocket';
import { notificationSound } from '../../../lib/notificationSound';

export function useConversations() {
  const queryClient = useQueryClient();
  const {
    filter,
    setConversations,
    addConversation,
    updateConversation,
    removeConversation,
  } = useConversationStore();
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [extraConversations, setExtraConversations] = useState<Conversation[]>([]);

  const { data: conversations, isLoading } = useQuery({
    queryKey: ['conversations', filter],
    queryFn: async () => {
      const data = await conversationService.getConversations(filter);
      setHasMore(data.length >= CONVERSATION_PAGE_SIZE);
      setExtraConversations([]);
      // Keep the open conversation even when it isn't on this page (e.g. an
      // older one opened from a /conversations/:id link). Otherwise the chat
      // view would lose it on every refetch.
      const { selectedConversationId, getConversation } = useConversationStore.getState();
      const open =
        selectedConversationId && !data.some((c) => c.id === selectedConversationId)
          ? getConversation(selectedConversationId)
          : undefined;
      setConversations(open ? [open, ...data] : data);
      return data;
    },
  });

  // --- Real-time socket listeners ---

  // New conversation created (e.g. new WhatsApp customer)
  useSocketEvent(SOCKET_EVENTS.CONVERSATION_NEW, (data) => {
    addConversation(data as Conversation);
    notificationSound.playConversation();
  });

  // Conversation updated (state change, priority, labels, assignment, etc.)
  useSocketEvent(SOCKET_EVENTS.CONVERSATION_UPDATED, (data) => {
    const conv = data.conversation as Conversation;
    updateConversation(conv.id, conv);
    if (conv.state === 'awaiting_staff') {
      notificationSound.playUrgent();
    }
  });

  // Conversation assigned
  useSocketEvent(SOCKET_EVENTS.CONVERSATION_ASSIGNED, (data) => {
    updateConversation(data.conversationId, {
      assignedStaffId: data.staffId,
      state: 'staff_active',
    });
  });

  // Conversation resolved
  useSocketEvent(SOCKET_EVENTS.CONVERSATION_RESOLVED, (data) => {
    updateConversation(data.conversationId, { state: 'resolved' });
  });

  // Agent failure — alert staff
  useSocketEvent(SOCKET_EVENTS.AGENT_ERROR, (data) => {
    toast.error(`AI agent failed: ${data.error}`, { duration: 8000 });
    notificationSound.playUrgent();
  });

  // Sentiment alert — notify staff of negative sentiment
  useSocketEvent(SOCKET_EVENTS.SENTIMENT_ALERT, (data) => {
    const conv = useConversationStore.getState().conversations.find(
      (c) => c.id === data.conversationId
    );
    const customerName = conv?.customerName || 'Customer';
    const category = data.category.replace(/_/g, ' ');
    toast.error(
      `Negative sentiment: ${customerName} (${category})`,
      { duration: 6000 }
    );
    notificationSound.playUrgent();
  });

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const currentTotal = (conversations?.length || 0) + extraConversations.length;
    setLoadingMore(true);
    try {
      const more = await conversationService.getConversations(filter, currentTotal);
      if (more.length > 0) {
        setExtraConversations((prev) => [...prev, ...more]);
        setConversations([...(conversations || []), ...extraConversations, ...more]);
      }
      setHasMore(more.length >= CONVERSATION_PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, conversations, extraConversations, filter, setConversations]);

  const assignMutation = useMutation({
    mutationFn: ({ id, staffId }: { id: string; staffId: string }) =>
      conversationService.assignConversation(id, staffId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversation assigned');
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => conversationService.resolveConversation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Conversation resolved');
    },
  });

  const returnToBotMutation = useMutation({
    mutationFn: (id: string) => conversationService.returnToBot(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Returned to bot');
    },
  });

  const updatePriorityMutation = useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: string }) =>
      conversationService.updatePriority(id, priority),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Priority updated');
    },
  });

  const updateLabelsMutation = useMutation({
    mutationFn: ({ id, labels }: { id: string; labels: string[] }) =>
      conversationService.updateLabels(id, labels),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      toast.success('Labels updated');
    },
  });

  return {
    conversations: conversations || [],
    isLoading,
    assignConversation: assignMutation.mutate,
    resolveConversation: resolveMutation.mutate,
    returnToBot: returnToBotMutation.mutate,
    updatePriority: updatePriorityMutation.mutate,
    updateLabels: updateLabelsMutation.mutate,
    hasMore,
    loadMore,
    loadingMore,
  };
}
